import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';

import type { NativeOutboxEvent } from '../chat/platform/nativeOutbox';

interface MaperNativePlugin {
    /** Register pending FCM token with backend (call after login).
     *  Reads auth token lazily from cookie store — no eager sync needed. */
    registerPendingFcmToken(): Promise<void>;
    /** Return the same native device id used for FCM token registration. */
    getDeviceId(): Promise<{ device_id: string | null }>;
    /** iOS only: keep one background drain queued behind the JS engine's sends. */
    scheduleOutboxDrain(): Promise<void>;
    /** Android only: run the native drainer now. It is the outbox's one sender there. */
    drainOutbox(): Promise<void>;
    /** Android only: stop the native drainer and delete the chat database and media (logout). */
    wipeChat(): Promise<void>;
    /** Android only: every row the native drainer settles. */
    addListener(event: 'outboxChanged', listener: (event: NativeOutboxEvent) => void): Promise<PluginListenerHandle>;
    /** Open this app's notification settings (S9F). Android lands on the app's notification
     *  screen; iOS has no deep link past the app's own settings page. Rejects when the OS
     *  refuses to open it, so a caller can tell "they are looking at it" from "they are not". */
    openNotificationSettings(): Promise<void>;
    /** What the phone permits, as the OS reports it (S9F): `granted`, `denied`, or — iOS only —
     *  `not_determined`. The same value the device sends to the backend on registration, and the
     *  only one that reflects the Settings toggle: Capacitor's `checkPermissions()` answers
     *  `granted` outright on Android below API 33. */
    getNotificationPermission(): Promise<{ permission: 'granted' | 'denied' | 'not_determined' }>;
    /** Open this app's page in the OS settings, so a blocked permission can be re-enabled.
     *  Rejects when the settings screen cannot be launched. */
    openAppSettings(): Promise<void>;
    /** Android only. Fit a captured photo for sending and store it, with its preview, in the chat media
     *  folder (Directory.Data-relative paths). The image never crosses into JS: only paths and sizes
     *  return. EXIF is kept, the pixels are turned upright, and the camera's temporary copy removed.
     *  `capturedNow` marks a photo the camera just took, which gets the phone's EXIF time offset. */
    preparePhoto(options: { source: string; capturedNow: boolean }): Promise<{
        path: string;
        previewPath: string;
        size: number;
        width: number;
        height: number;
    }>;
}

const MaperNative = registerPlugin<MaperNativePlugin>('MaperNative');

export default MaperNative;
